import { WebringData } from "../../types/webring";
import { fetchWebringData, validateWebringData } from "./fetchWebringData";

/**
 * Web Component wrapper for the webring widget
 * Usage: <webring-widget data-source="url" size="small|medium" theme="auto"></webring-widget>
 */
class WebringElement extends HTMLElement {
  private shadow: ShadowRoot;
  private data: WebringData | null = null;
  private loading = false;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return ["data-source", "size", "theme", "position"];
  }

  async connectedCallback(): Promise<void> {
    await this.loadData();
    // Re-render on system theme change
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
      if (this.getAttribute("theme") === "auto") this.updateTheme();
    });
  }

  async attributeChangedCallback(
    name: string,
    oldValue: string,
    newValue: string,
  ): Promise<void> {
    if (oldValue === newValue) return;

    if (name === "data-source") {
      this.data = null;
      await this.loadData();
    } else if (name === "size") {
      this.updateSize(newValue as "small" | "medium");
    } else if (name === "theme") {
      this.updateTheme();
    } else if (name === "position") {
      this.updatePosition(newValue);
    }
  }

  private async loadData(): Promise<void> {
    if (this.loading) return;
    const source = this.getAttribute("data-source");
    if (!source) return;

    this.loading = true;
    try {
      const data = await fetchWebringData(source);
      if (validateWebringData(data)) {
        this.data = data;
        this.render(); // Initial render
      }
    } catch (e) {
      console.error("Webring failed to load", e);
    } finally {
      this.loading = false;
    }
  }

  private resolveTheme(): string {
    const themeAttr = this.getAttribute("theme") || "auto";
    if (themeAttr === "auto") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return themeAttr;
  }

  private getSize(): "small" | "medium" {
    const size = this.getAttribute("size") || "small";
    return size as "small" | "medium";
  }

  private updateSize(newSize: "small" | "medium"): void {
    const widget = this.shadow.querySelector(".widget");
    if (widget) {
      widget.setAttribute("data-size", newSize);
    }
  }

  private updateTheme(): void {
    // For theme changes, we might need to re-render if we use JS-based theme logic,
    // but ideally we'd use CSS variables.
    // Since our current render uses JS for colors, we'll re-render for theme only.
    this.render();
  }

  private updatePosition(pos: string): void {
    const widget = this.shadow.querySelector(".widget");
    if (widget) {
      widget.classList.toggle("is-left", pos.includes("left"));
      widget.classList.toggle("is-top", pos.includes("top"));
    }
  }

  private isLeft(): boolean {
    return (this.getAttribute("position") ?? "").includes("left");
  }

  private isTop(): boolean {
    return (this.getAttribute("position") ?? "").includes("top");
  }

  // Link-specific color mapping now comes from data (webring.json)
  private getLinkColor(link: WebringData["links"][number]): string {
    if (link.color) return link.color;
    const url = link.url;
    if (url.includes("github.com")) return "#6e5494";
    if (url.includes("twitter.com") || url.includes("x.com")) return "#1da1f2";
    if (url.includes("mastodon")) return "#6364ff";
    if (url.includes("linkedin")) return "#0077b5";
    if (url.includes("youtube")) return "#ff0000";
    if (url.includes("instagram")) return "#e4405f";
    if (url.includes("strangerloops")) return "#ff6b6b";
    if (url.includes("svnr")) return "#48D2F4";
    return "#8b5cf6"; // Default purple
  }

  private render(): void {
    if (!this.data) return;

    const theme = this.resolveTheme();
    const isDark = theme === "dark";
    const initialSize = this.getSize();

    const linkStyles = this.data.links
      .map((link, i) => {
        const color = this.getLinkColor(link);
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `
      .link-${i} {
        color: ${color};
      }
      .link-${i}:hover {
        background: rgba(${r}, ${g}, ${b}, ${isDark ? "0.2" : "0.15"});
        border-left: 3px solid ${color};
        padding-left: calc(0.75em - 3px);
      }
    `;
      })
      .join("");

    const style = `
      <style>
        :host {
          display: block;
          font-family: system-ui, sans-serif;
          /* Responsive positioning — can be overridden via CSS custom properties */
          --widget-bottom: 18px;
          --widget-right: 18px;
          --widget-top: auto;
          --widget-left: auto;
          position: fixed;
          bottom: var(--widget-bottom);
          right: var(--widget-right);
          top: var(--widget-top);
          left: var(--widget-left);
          z-index: 1000;
          --glass-bg: ${isDark ? "rgba(20, 20, 20, 0.6)" : "rgba(255, 255, 255, 0.7)"};
          --glass-border: ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.5)"};
          --text: ${isDark ? "#e0e0e0" : "#1a1a1a"};
          --text-muted: ${isDark ? "#a0a0a0" : "#666"};
          --hover-bg: ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)"};
          --shadow: ${isDark ? "0 8px 32px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3)" : "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)"};
          --spring: cubic-bezier(0.34, 1.26, 0.64, 1);
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-4px) scale(1.01); }
        }

        .handle {
          width: 3.5em;
          height: 3.5em;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.2)"};
          transform: rotate(40deg);
        }

        .widget[data-size="medium"] .handle {
          transform: rotate(-135deg);
        }

        .handle:hover {
          transform: rotate(45deg) scale(1.15);
          color: ${isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.35)"};
        }

        .widget[data-size="medium"] .handle:hover {
          transform: rotate(-135deg) scale(1.15);
        }

        .handle:active {
          transform: scale(0.9);
        }

        .widget {
          background: var(--glass-bg);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid var(--glass-border);
          box-shadow: var(--shadow);
          color: var(--text);
          font-family: 'SF Pro Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          font-size: clamp(14px, 2.5vw, 16px);
          position: relative;
          overflow: hidden;
          will-change: width, height, border-radius;
          display: inline-block;
          width: fit-content;
          max-width: 100%;
          box-sizing: border-box;
          border-radius: 12px;
          transition: box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.4s ease;
        }

        .widget:hover {
          box-shadow: ${isDark ? "0 2px 6px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)" : "0 2px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.08)"};
        }

        /* Size Variants */
        .widget[data-size="small"] {
          width: 40px;
          height: 40px;
          min-width: 40px;
          min-height: 40px;
          border-radius: 14px;
        }

        .widget[data-size="medium"] {
          display: flex;
          flex-direction: column;
          height: auto;
          min-height: 64px;
          border-radius: 20px;
        }

        .widget.is-top[data-size="medium"] {
          flex-direction: column-reverse;
        }

        @keyframes bounce-lift {
          0%   { transform: translateY(0); }
          40%  { transform: translateY(-2px); }
          100% { transform: translateY(0); }
        }

        .widget:hover {
          box-shadow: ${isDark ? "0 12px 48px rgba(0, 0, 0, 0.6), 0 4px 12px rgba(0, 0, 0, 0.4)" : "0 12px 48px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)"};
          border-color: ${isDark ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.7)"};
        }

        /* Logo Layout */
        .logo-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          user-select: none;
          transition: padding 0.6s var(--spring);
        }

        .widget[data-size="medium"] .logo-container {
          padding: 0 0.5em 0 1em;
          border-top: 1px solid var(--glass-border);
        }

        .widget.is-top[data-size="medium"] .logo-container {
          border-top: none;
          border-bottom: 1px solid var(--glass-border);
        }

        .logo-nib {
          font-size: 2.4rem;
          line-height: 1.2;
        }

        .widget[data-size="small"] .logo-nib {
          font-size: 1.2rem;
        }
        
        .logo-text {
          font-family: 'SF Pro Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          font-size: 1.326em;
          font-weight: 600;
          color: var(--text);
          text-decoration: none;
          text-indent: 1em;
          transition: opacity 0.2s ease;
          display: inline-block;
          width: 8em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .widget[data-size="small"] .logo-container {
          justify-content: center;
          padding: 0;
          width: 40px;
          height: 40px;
        }

        .widget.is-left .logo-container {
          flex-direction: row-reverse;
        }

        .widget[data-size="small"] .logo-text {
          display: none;
        }

        .widget[data-size="medium"] .logo-text {
          display: inline-block;
        }

        .logo-link:hover .logo-text {
          opacity: 0.8;
        }

        /* Links List */
        .links {
          list-style: none;
          padding: 0.5rem;
          margin: 0;
          overflow: hidden;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
        }

        /* Emoji-only grid: hide text labels */
        .links a .link-label {
          display: none;
        }

        .widget[data-size="small"] .links {
          display: none;
          opacity: 0;
        }

        .widget[data-size="medium"] .links {
          opacity: 1;
        }

        .links li {
          margin: 0;
          opacity: 0;
          animation: fade-in-up 0.4s var(--spring) forwards;
        }

        ${this.data.links.map((_, i) => `.links li:nth-child(${i + 1}) { animation-delay: ${0.05 * i}s; }`).join("\n        ")}

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .links a {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4em;
          padding: 0.45em 0.8em;
          text-decoration: none;
          font-size: 0.85em;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 999px;
          min-width: 44px;
          min-height: 44px;
          box-sizing: border-box;
        }

        .widget[data-size="medium"] .links a {
          font-size: 0.95em;
          padding: 0.6em 1em;
          min-height: 44px; /* Ensure tap target */
        }

        .links a span.link-label {
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .links a:hover {
          background: ${isDark ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.06)"};
          box-shadow: inset 0 1px 3px ${isDark ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.12)"}, inset 0 1px 1px ${isDark ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.08)"};
        }

        .links a:active {
          background: ${isDark ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.1)"};
          box-shadow: inset 0 2px 4px ${isDark ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.15)"};
          transform: scale(0.97);
          transition: transform 0.1s ease;
        }

        @keyframes emoji-bounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }

        .link-emoji {
          font-size: 1.4em;
          flex-shrink: 0;
          transition: transform 0.3s var(--spring);
        }

        .links a:hover .link-emoji {
          animation: emoji-bounce 0.5s var(--spring);
        }

        ${linkStyles}

        @media (hover: none) {
          .widget:hover {
            box-shadow: var(--shadow);
          }
        }
      </style>
    `;

    const linksHtml = `
      <ul class="links">
        ${this.data.links
          .map(
            (link, i) => {
              const color = this.getLinkColor(link);
              return `
          <li>
            <a href="${link.url}" class="link-${i}" data-name="${link.name}" data-color="${color}" title="${link.description || link.name}" target="_blank">
              <span class="link-emoji">${link.emoji ?? "🔗"}</span>
              <span class="link-label">${link.name}</span>
            </a>
          </li>
        `;
            },
          )
          .join("")}
      </ul>
    `;

    const handleSvg = `<svg width="100%" height="100%" viewBox="0 0 80.252602 81.155724" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(-10.698367,-11.054825)">
        <path d="m 143.65518,206.83961 c 2.41591,10.33473 -49.397142,58.91822 -59.555232,55.8431 -10.15809,-3.07513 -26.326103,-72.23829 -18.583923,-79.49789 7.742179,-7.2596 75.723245,13.32007 78.139155,23.65479 z" transform="translate(-52.786019,-170.61192)" />
      </g>
    </svg>`;

    const html = `
      ${style}
      <div class="widget${this.isLeft() ? " is-left" : ""}${this.isTop() ? " is-top" : ""}" data-size="${initialSize}">
        ${linksHtml}
        <div class="logo-container">
          <a href="https://kerry.ink" class="logo-link" target="_blank" style="display: flex; align-items: center; gap: 0.5em; text-decoration: none; color: inherit;">
            <span class="logo-text">kerry.ink</span>
          </a>
          <div class="handle"><span class="logo-nib">✒︎</span></div>
        </div>
      </div>
    `;

    this.shadow.innerHTML = html;

    // Emoji grid hover: change logo text to site name & tint widget background
    const logoText = this.shadow.querySelector(".logo-text");
    const widget = this.shadow.querySelector(".widget");
    this.shadow.querySelectorAll(".links a").forEach(a => {
      const siteName = a.getAttribute("data-name");
      const colorHex = a.getAttribute("data-color");
      // Precompute background tint: dark = colored glow, light = pastel lightening
      let bgColor = null;
      if (colorHex) {
        const r = parseInt(colorHex.slice(1, 3), 16);
        const g = parseInt(colorHex.slice(3, 5), 16);
        const b = parseInt(colorHex.slice(5, 7), 16);
        bgColor = isDark
          ? `rgba(${r}, ${g}, ${b}, 0.18)`
          : `rgba(${Math.round(r * 0.2 + 255 * 0.8)}, ${Math.round(g * 0.2 + 255 * 0.8)}, ${Math.round(b * 0.2 + 255 * 0.8)}, 0.6)`;
      }
      a.addEventListener("mouseenter", () => {
        if (logoText && siteName) logoText.textContent = siteName;
        if (widget && bgColor) (widget as HTMLElement).style.background = bgColor;
      });
      a.addEventListener("mouseleave", () => {
        if (logoText) logoText.textContent = "kerry.ink";
        if (widget) (widget as HTMLElement).style.background = "";
      });
    });

    const handle = this.shadow.querySelector(".handle");

    // Add click handler for handle to cycle sizes
    handle?.addEventListener("click", (e) => {
      e.stopPropagation();
      const currentSize = this.getSize();
      const nextSize =
        currentSize === "small" ? "medium" : "small";

      // Update attribute on host (which updates data-size on inner widget via attributeChangedCallback)
      this.setAttribute("size", nextSize);
    });
  }
}

if (!customElements.get("webring-widget")) {
  customElements.define("webring-widget", WebringElement);
}
